import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import { Driver } from '../fleet/driver/driver.model';
import { SessionStorageService } from '../services/session-storage.service';
import { ToolbarService } from '../services/toolbar.service';
import {
  WarehousingBillingDraft,
  WarehousingBulkPreview,
  WarehousingSupplyChoice,
  WarehousingSupplyLineOptions,
  WarehousingIncomingClaim,
  WarehousingSupplyPreview,
  WarehousingBulkRequest,
  WarehousingBulkResult,
  WarehousingDestruction,
  WarehousingDispatch,
  WarehousingDispatchScanCommand,
  WarehousingHubLane,
  WarehousingInboundReceipt,
  WarehousingInboundReceiptAttachment,
  WarehousingItem,
  WarehousingTrackingPreview,
  WarehousingTrackingResult,
  WarehousingItemSummaryPage,
  WarehousingMasterData,
  WarehousingBulkReceiveCommand,
  WarehousingManualGoodsOutCommand,
  WarehousingGoodsOutAvailability,
  WarehousingReservationAllocationCommand,
  WarehousingReservationBulkAllocationCommand,
  WarehousingReservationRequest,
  WarehousingRefurbishment,
  WarehousingSearchFilters,
  WarehousingShipmentWork,
  WarehousingWorkDirectoryPage,
  WarehousingInboundNotice,
  WarehousingReturnRequest,
  WarehousingShipmentInboundRequest,
  WarehousingShipmentInboundReceiveLine,
  WarehousingReturnReceiveLine,
  WarehousingWorkHistory,
  WarehousingStockEntry,
  WarehousingStockEntryCorrectionCommand,
  WarehousingItemTransactionLedgerPage,
  WarehousingStockMovement,
  WarehousingStockLot,
  WarehousingStockReportResponse,
  WarehousingStockSummary,
  WarehousingStockUnit,
  WarehousingStockUnitLabelRequest,
  WarehousingUnitReturnCommand,
  WarehousingStorageRate,
  WarehousingTransfer,
  WarehousingTransferScanCommand
} from './warehousing.model';

@Injectable({ providedIn: 'root' })
export class WarehousingService {
  private http = inject(HttpClient);
  private toolbarService = inject(ToolbarService);
  private sessionStorageService = inject(SessionStorageService);
  private apiUrl = `${environment.restApiUrl}warehousing`;

  getCompanyId(): number {
    const company = this.sessionStorageService.getCompany();
    return company?.id || this.toolbarService.customer()?.company?.id || 0;
  }

  getItems(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingItem[]>> {
    return this.http.get<APIResponseWrapper<WarehousingItem[]>>(`${this.apiUrl}/items`, { params: this.searchParams(filters) });
  }

  getItemSummaries(filters: WarehousingSearchFilters | undefined, page: number, size: number): Observable<APIResponseWrapper<WarehousingItemSummaryPage>> {
    return this.http.get<APIResponseWrapper<WarehousingItemSummaryPage>>(`${this.apiUrl}/items/summary`, {
      params: this.searchParams(filters).set('page', page).set('size', size)
    });
  }

  getItemByBarcode(barcode: string): Observable<APIResponseWrapper<WarehousingItem>> {
    return this.http.get<APIResponseWrapper<WarehousingItem>>(`${this.apiUrl}/items/by-barcode`, {
      params: this.companyParams().set('barcode', barcode)
    });
  }

  createItem(item: WarehousingItem): Observable<APIResponseWrapper<WarehousingItem>> {
    return this.http.post<APIResponseWrapper<WarehousingItem>>(`${this.apiUrl}/items`, item, { params: this.companyParams() });
  }

  updateItem(item: WarehousingItem): Observable<APIResponseWrapper<WarehousingItem>> {
    return this.http.put<APIResponseWrapper<WarehousingItem>>(`${this.apiUrl}/items/${item.id}`, item, { params: this.companyParams() });
  }

  previewTrackingChange(itemId: number, toMode: 'QUANTITY' | 'UNIT'): Observable<APIResponseWrapper<WarehousingTrackingPreview>> {
    return this.http.get<APIResponseWrapper<WarehousingTrackingPreview>>(`${this.apiUrl}/items/${itemId}/tracking-change/preview`, {
      params: this.companyParams().set('toMode', toMode)
    });
  }

  confirmTrackingChange(itemId: number, preview: WarehousingTrackingPreview, reason: string): Observable<APIResponseWrapper<WarehousingTrackingResult>> {
    return this.http.post<APIResponseWrapper<WarehousingTrackingResult>>(`${this.apiUrl}/items/${itemId}/tracking-change/confirm`, {
      toMode: preview.toMode, snapshot: preview.snapshot, reason
    }, { params: this.companyParams() });
  }

  previewTrackingRepair(itemId: number): Observable<APIResponseWrapper<WarehousingTrackingPreview>> {
    return this.http.get<APIResponseWrapper<WarehousingTrackingPreview>>(`${this.apiUrl}/items/${itemId}/tracking-repair/preview`, {
      params: this.companyParams()
    });
  }

  confirmTrackingRepair(itemId: number, preview: WarehousingTrackingPreview, reason: string): Observable<APIResponseWrapper<WarehousingTrackingResult>> {
    return this.http.post<APIResponseWrapper<WarehousingTrackingResult>>(`${this.apiUrl}/items/${itemId}/tracking-repair/confirm`, {
      snapshot: preview.snapshot, reason
    }, { params: this.companyParams() });
  }

  getMasterData(type: string, filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingMasterData[]>> {
    return this.http.get<APIResponseWrapper<WarehousingMasterData[]>>(`${this.apiUrl}/master-data/${type}`, { params: this.searchParams(filters) });
  }

  saveMasterData(data: WarehousingMasterData): Observable<APIResponseWrapper<WarehousingMasterData>> {
    if (data.id) {
      return this.http.put<APIResponseWrapper<WarehousingMasterData>>(`${this.apiUrl}/master-data/${data.type}/${data.id}`, data, { params: this.companyParams() });
    }
    return this.http.post<APIResponseWrapper<WarehousingMasterData>>(`${this.apiUrl}/master-data/${data.type}`, data, { params: this.companyParams() });
  }

  getHubLanes(): Observable<APIResponseWrapper<WarehousingHubLane[]>> {
    return this.http.get<APIResponseWrapper<WarehousingHubLane[]>>(`${this.apiUrl}/hub-lanes`, {
      params: this.companyParams()
    });
  }

  saveHubLane(lane: WarehousingHubLane): Observable<APIResponseWrapper<WarehousingHubLane>> {
    return this.http.post<APIResponseWrapper<WarehousingHubLane>>(`${this.apiUrl}/hub-lanes`, lane, {
      params: this.companyParams()
    });
  }

  deactivateHubLane(id: number): Observable<APIResponseWrapper<WarehousingHubLane>> {
    return this.http.post<APIResponseWrapper<WarehousingHubLane>>(`${this.apiUrl}/hub-lanes/${id}/deactivate`, {}, {
      params: this.companyParams()
    });
  }

  createStockEntry(entry: WarehousingStockEntry): Observable<APIResponseWrapper<WarehousingStockEntry>> {
    return this.http.post<APIResponseWrapper<WarehousingStockEntry>>(`${this.apiUrl}/stock-entries`, this.normalizeDates(entry), { params: this.companyParams() });
  }

  searchReceivingDrivers(search = '', page = 0, selectedId?: number | null): Observable<APIListWrapper<Driver>> {
    let params = this.companyParams().set('search', search).set('page', page).set('size', 50);
    if (selectedId) params = params.set('selectedId', selectedId);
    return this.http.get<APIListWrapper<Driver>>(`${this.apiUrl}/driver-options`, { params });
  }

  getStockEntry(id: number): Observable<APIResponseWrapper<WarehousingStockEntry>> {
    return this.http.get<APIResponseWrapper<WarehousingStockEntry>>(`${this.apiUrl}/stock-entries/${id}`, {
      params: this.companyParams()
    });
  }

  updateStockEntry(id: number, entry: WarehousingStockEntry): Observable<APIResponseWrapper<WarehousingStockEntry>> {
    return this.http.put<APIResponseWrapper<WarehousingStockEntry>>(`${this.apiUrl}/stock-entries/${id}`,
      this.normalizeDates(entry), { params: this.companyParams() });
  }

  correctStockEntry(id: number, command: WarehousingStockEntryCorrectionCommand): Observable<APIResponseWrapper<WarehousingStockEntry>> {
    return this.http.post<APIResponseWrapper<WarehousingStockEntry>>(`${this.apiUrl}/stock-entries/${id}/corrections`, {
      ...command,
      stockEntry: this.normalizeDates(command.stockEntry)
    }, { params: this.companyParams() });
  }

  getStockEntries(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStockEntry[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStockEntry[]>>(`${this.apiUrl}/stock-entries`, { params: this.searchParams(filters) });
  }

  bulkReceiveStock(command: WarehousingBulkReceiveCommand): Observable<APIResponseWrapper<WarehousingInboundReceipt>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundReceipt>>(`${this.apiUrl}/stock-entries/bulk-receive`,
      this.normalizeDates(command), { params: this.companyParams() });
  }

  getInboundReceipts(): Observable<APIResponseWrapper<WarehousingInboundReceipt[]>> {
    return this.http.get<APIResponseWrapper<WarehousingInboundReceipt[]>>(`${this.apiUrl}/inbound-receipts`, {
      params: this.companyParams()
    });
  }

  getInboundReceipt(id: number): Observable<APIResponseWrapper<WarehousingInboundReceipt>> {
    return this.http.get<APIResponseWrapper<WarehousingInboundReceipt>>(`${this.apiUrl}/inbound-receipts/${id}`, {
      params: this.companyParams()
    });
  }

  addInboundReceiptAttachment(id: number, attachment: WarehousingInboundReceiptAttachment): Observable<APIResponseWrapper<WarehousingInboundReceipt>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundReceipt>>(`${this.apiUrl}/inbound-receipts/${id}/attachments`, attachment, {
      params: this.companyParams()
    });
  }

  downloadGrnReport(inboundReceiptId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/inbound-receipts/${inboundReceiptId}/report/download`, {
      params: this.companyParams(),
      responseType: 'blob'
    });
  }

  getStockSummary(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStockSummary[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStockSummary[]>>(`${this.apiUrl}/stock-summary`, { params: this.searchParams(filters) });
  }

  getStockUnits(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStockUnit[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStockUnit[]>>(`${this.apiUrl}/stock-units`, { params: this.searchParams(filters) });
  }

  getStockUnitByCode(unitCode: string): Observable<APIResponseWrapper<WarehousingStockUnit>> {
    return this.http.get<APIResponseWrapper<WarehousingStockUnit>>(`${this.apiUrl}/stock-units/by-code`, {
      params: this.companyParams().set('unitCode', unitCode)
    });
  }

  returnUnit(command: WarehousingUnitReturnCommand): Observable<APIResponseWrapper<WarehousingStockUnit>> {
    return this.http.post<APIResponseWrapper<WarehousingStockUnit>>(`${this.apiUrl}/stock-units/return`, command, {
      params: this.companyParams()
    });
  }

  getStockMovements(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStockMovement[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStockMovement[]>>(`${this.apiUrl}/stock-movements`, { params: this.searchParams(filters) });
  }

  getItemTransactions(filters?: WarehousingSearchFilters, page = 0, size = 25): Observable<APIResponseWrapper<WarehousingItemTransactionLedgerPage>> {
    return this.http.get<APIResponseWrapper<WarehousingItemTransactionLedgerPage>>(`${this.apiUrl}/item-transactions`, {
      params: this.searchParams(filters).set('page', page).set('size', size)
    });
  }

  exportItemTransactions(filters: WarehousingSearchFilters, format: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/item-transactions/export`, this.normalizeDates(filters), {
      params: this.companyParams().set('format', format), responseType: 'blob'
    });
  }

  getStockLots(): Observable<APIResponseWrapper<WarehousingStockLot[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStockLot[]>>(`${this.apiUrl}/lots`, {
      params: this.companyParams()
    });
  }

  getStockLot(id: number): Observable<APIResponseWrapper<WarehousingStockLot>> {
    return this.http.get<APIResponseWrapper<WarehousingStockLot>>(`${this.apiUrl}/lots/${id}`, {
      params: this.companyParams()
    });
  }

  releaseStockLot(id: number, notes?: string): Observable<APIResponseWrapper<WarehousingStockLot>> {
    return this.http.post<APIResponseWrapper<WarehousingStockLot>>(`${this.apiUrl}/lots/${id}/release`,
      { actorId: this.sessionStorageService.getCustomer()?.id, notes }, { params: this.companyParams() });
  }

  rejectStockLot(id: number, notes?: string): Observable<APIResponseWrapper<WarehousingStockLot>> {
    return this.http.post<APIResponseWrapper<WarehousingStockLot>>(`${this.apiUrl}/lots/${id}/reject`,
      { actorId: this.sessionStorageService.getCustomer()?.id, notes }, { params: this.companyParams() });
  }

  getStockUnitMovements(stockUnitId: number, filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStockMovement[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStockMovement[]>>(`${this.apiUrl}/stock-units/${stockUnitId}/movements`, {
      params: this.searchParams(filters)
    });
  }

  exportStockMovements(filters: WarehousingSearchFilters, format: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/stock-movements/export`, this.normalizeDates(filters), {
      params: this.companyParams().set('format', format),
      responseType: 'blob'
    });
  }

  getReservationRequests(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingReservationRequest[]>> {
    return this.http.get<APIResponseWrapper<WarehousingReservationRequest[]>>(`${this.apiUrl}/reservations`, { params: this.searchParams(filters) });
  }

  getReservationRequest(requestId: number): Observable<APIResponseWrapper<WarehousingReservationRequest | null>> {
    return this.http.get<APIResponseWrapper<WarehousingReservationRequest | null>>(
      `${this.apiUrl}/reservations/${requestId}`, { params: this.companyParams() });
  }

  reserveReservationAllocation(requestId: number, command: WarehousingReservationAllocationCommand): Observable<APIResponseWrapper<WarehousingReservationRequest>> {
    return this.http.post<APIResponseWrapper<WarehousingReservationRequest>>(`${this.apiUrl}/reservations/${requestId}/allocations`,
      this.normalizeDates(command), { params: this.companyParams() });
  }

  reserveAllReservationAllocations(requestId: number, command?: WarehousingReservationBulkAllocationCommand): Observable<APIResponseWrapper<WarehousingReservationRequest>> {
    return this.http.post<APIResponseWrapper<WarehousingReservationRequest>>(`${this.apiUrl}/reservations/${requestId}/allocations/reserve-all`,
      this.normalizeDates(command || {}), { params: this.companyParams() });
  }

  releaseReservationAllocation(requestId: number, allocationId: number): Observable<APIResponseWrapper<WarehousingReservationRequest>> {
    return this.http.delete<APIResponseWrapper<WarehousingReservationRequest>>(`${this.apiUrl}/reservations/${requestId}/allocations/${allocationId}`,
      { params: this.companyParams() });
  }

  getStockReport(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStockReportResponse>> {
    return this.http.get<APIResponseWrapper<WarehousingStockReportResponse>>(`${this.apiUrl}/stock-report`, {
      params: this.searchParams(filters).set('compactGroups', 'true')
    });
  }

  exportStockReport(filters: WarehousingSearchFilters, format: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/stock-report/export`, this.normalizeDates(filters), {
      params: this.companyParams().set('format', format),
      responseType: 'blob'
    });
  }

  exportWarehouseStockAudit(filters: WarehousingSearchFilters): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/stock-report/audit-export`, this.normalizeDates(filters), {
      params: this.companyParams(),
      responseType: 'blob'
    });
  }

  getDispatches(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingDispatch[]>> {
    return this.http.get<APIResponseWrapper<WarehousingDispatch[]>>(`${this.apiUrl}/dispatches`, { params: this.searchParams(filters) });
  }

  getDispatch(dispatchId: number): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.get<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}`, { params: this.companyParams() });
  }

  downloadGdnReport(dispatchId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/dispatches/${dispatchId}/report/download`, {
      params: this.companyParams(), responseType: 'blob'
    });
  }

  createManualGoodsOut(command: WarehousingManualGoodsOutCommand): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/manual-goods-out`,
      command, { params: this.companyParams() });
  }

  getGoodsOutAvailability(itemId: number, crmClientId?: number, crmAccountId?: number): Observable<APIResponseWrapper<WarehousingGoodsOutAvailability[]>> {
    let params = this.companyParams().set('itemId', itemId);
    if (crmClientId) params = params.set('crmClientId', crmClientId);
    if (crmAccountId) params = params.set('crmAccountId', crmAccountId);
    return this.http.get<APIResponseWrapper<WarehousingGoodsOutAvailability[]>>(
      `${this.apiUrl}/dispatches/manual-goods-out/availability`, { params });
  }

  createDispatch(dispatch: WarehousingDispatch): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches`, this.normalizeDates(dispatch), { params: this.companyParams() });
  }

  getOutboundSupplyOptions(type: 'DISPATCH' | 'TRANSFER', id: number): Observable<APIResponseWrapper<WarehousingSupplyLineOptions[]>> {
    return this.http.get<APIResponseWrapper<WarehousingSupplyLineOptions[]>>(
      `${this.apiUrl}/outbound-supply/${type}/${id}/options`, { params: this.companyParams() });
  }

  getOutboundSupplyClaims(type: 'DISPATCH' | 'TRANSFER', id: number): Observable<APIResponseWrapper<WarehousingIncomingClaim[]>> {
    return this.http.get<APIResponseWrapper<WarehousingIncomingClaim[]>>(
      `${this.apiUrl}/outbound-supply/${type}/${id}/claims`, { params: this.companyParams() });
  }

  previewOutboundSupply(type: 'DISPATCH' | 'TRANSFER', id: number, choices: WarehousingSupplyChoice[]):
      Observable<APIResponseWrapper<WarehousingSupplyPreview>> {
    return this.http.post<APIResponseWrapper<WarehousingSupplyPreview>>(`${this.apiUrl}/outbound-supply/preview`,
      { documentType: type, documentId: id, choices }, { params: this.companyParams() });
  }

  confirmOutboundSupply(previewToken: string, reason: string, idempotencyKey: string):
      Observable<APIResponseWrapper<{ batchId: string; claims: WarehousingIncomingClaim[] }>> {
    return this.http.post<APIResponseWrapper<{ batchId: string; claims: WarehousingIncomingClaim[] }>>(
      `${this.apiUrl}/outbound-supply/confirm`, { previewToken, reason, idempotencyKey },
      { params: this.companyParams() });
  }

  releaseOutboundSupplyClaim(id: number, reason: string): Observable<APIResponseWrapper<WarehousingIncomingClaim>> {
    return this.http.post<APIResponseWrapper<WarehousingIncomingClaim>>(
      `${this.apiUrl}/outbound-supply/claims/${id}/release`, { reason }, { params: this.companyParams() });
  }

  reserveDispatch(dispatchId: number): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/reserve`, {}, { params: this.companyParams() });
  }

  markDispatchPicked(dispatchId: number, reason: string): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/mark-picked`, { reason }, { params: this.companyParams() });
  }

  markDispatchStaged(dispatchId: number, reason: string): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/mark-staged`, { reason }, { params: this.companyParams() });
  }

  markDispatchLoaded(dispatchId: number, reason: string): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/mark-loaded`, { reason }, { params: this.companyParams() });
  }

  scanDispatchPick(dispatchId: number, command: WarehousingDispatchScanCommand): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/scan-pick`,
      command, { params: this.companyParams() });
  }

  scanDispatchStage(dispatchId: number, command: WarehousingDispatchScanCommand): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/scan-stage`,
      command, { params: this.companyParams() });
  }

  scanDispatchLoad(dispatchId: number, command: WarehousingDispatchScanCommand): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/scan-load`,
      command, { params: this.companyParams() });
  }

  confirmDispatchExit(dispatchId: number, reason: string): Observable<APIResponseWrapper<WarehousingDispatch>> {
    return this.http.post<APIResponseWrapper<WarehousingDispatch>>(`${this.apiUrl}/dispatches/${dispatchId}/confirm-exit`, { reason }, { params: this.companyParams() });
  }

  getShipmentWork(shipmentOrDeliveryNumber: string): Observable<APIResponseWrapper<WarehousingShipmentWork>> {
    return this.http.get<APIResponseWrapper<WarehousingShipmentWork>>(`${this.apiUrl}/shipment-work`, {
      params: this.companyParams().set('shipmentNumber', shipmentOrDeliveryNumber)
    });
  }

  getShipmentWorkById(type: 'SHIPMENT' | 'DELIVERY', id: number): Observable<APIResponseWrapper<WarehousingShipmentWork>> {
    return this.http.get<APIResponseWrapper<WarehousingShipmentWork>>(`${this.apiUrl}/shipment-work/${type}/${id}`,
      { params: this.companyParams() });
  }

  getShipmentWorkDirectory(warehouseId: number, type: 'SHIPMENT' | 'DELIVERY',
    direction: 'INBOUND' | 'OUTBOUND', search: string,
    state: 'ALL' | 'SOURCE' | 'INBOUND' | 'EXCEPTION' | 'TRANSFER' | 'DISPATCH' | 'RETURN', page: number, size: number):
      Observable<APIResponseWrapper<WarehousingWorkDirectoryPage>> {
    return this.http.get<APIResponseWrapper<WarehousingWorkDirectoryPage>>(`${this.apiUrl}/shipment-work/directory`, {
      params: this.companyParams().set('warehouseId', warehouseId).set('type', type).set('direction', direction)
        .set('search', search).set('state', state).set('page', page).set('size', size)
    });
  }

  getInboundNotices(shipmentId: number, destinationWarehouseId?: number):
      Observable<APIResponseWrapper<WarehousingInboundNotice[]>> {
    let params = this.companyParams().set('shipmentId', shipmentId);
    if (destinationWarehouseId) params = params.set('destinationWarehouseId', destinationWarehouseId);
    return this.http.get<APIResponseWrapper<WarehousingInboundNotice[]>>(`${this.apiUrl}/shipment-work/notices`, { params });
  }

  getReturnRequests(shipmentId: number, warehouseId?: number):
      Observable<APIResponseWrapper<WarehousingReturnRequest[]>> {
    let params = this.companyParams().set('shipmentId', shipmentId);
    if (warehouseId) params = params.set('warehouseId', warehouseId);
    return this.http.get<APIResponseWrapper<WarehousingReturnRequest[]>>(`${this.apiUrl}/returns`, { params });
  }

  receiveReturn(id: number, lines: WarehousingReturnReceiveLine[]):
      Observable<APIResponseWrapper<WarehousingReturnRequest>> {
    return this.http.post<APIResponseWrapper<WarehousingReturnRequest>>(`${this.apiUrl}/returns/${id}/receive`,
      { lines }, { params: this.companyParams() });
  }

  getShipmentInbounds(shipmentId: number, warehouseId?: number):
      Observable<APIResponseWrapper<WarehousingShipmentInboundRequest[]>> {
    let params = this.companyParams().set('shipmentId', shipmentId);
    if (warehouseId) params = params.set('warehouseId', warehouseId);
    return this.http.get<APIResponseWrapper<WarehousingShipmentInboundRequest[]>>(
      `${this.apiUrl}/shipment-inbounds`, { params });
  }

  getShipmentInbound(id: number): Observable<APIResponseWrapper<WarehousingShipmentInboundRequest>> {
    return this.http.get<APIResponseWrapper<WarehousingShipmentInboundRequest>>(
      `${this.apiUrl}/shipment-inbounds/${id}`, { params: this.companyParams() });
  }

  receiveShipmentInbound(id: number, lines: WarehousingShipmentInboundReceiveLine[],
    reviewedManualReceipts: boolean): Observable<APIResponseWrapper<WarehousingShipmentInboundRequest>> {
    return this.http.post<APIResponseWrapper<WarehousingShipmentInboundRequest>>(
      `${this.apiUrl}/shipment-inbounds/${id}/receive`, { lines, reviewedManualReceipts },
      { params: this.companyParams() });
  }

  markInboundArrived(id: number): Observable<APIResponseWrapper<WarehousingInboundNotice>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundNotice>>(`${this.apiUrl}/shipment-work/notices/${id}/arrive`,
      {}, { params: this.companyParams() });
  }

  saveInboundCounts(id: number, counts: Array<{ groupKey: string; quantity: number; unitCodes: string[] }>):
      Observable<APIResponseWrapper<WarehousingInboundNotice>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundNotice>>(`${this.apiUrl}/shipment-work/notices/${id}/counts`,
      { counts, finalCount: true }, { params: this.companyParams() });
  }

  confirmInboundNotice(id: number, zoneId: number, noScanReason: string, idempotencyKey: string):
      Observable<APIResponseWrapper<WarehousingInboundNotice>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundNotice>>(`${this.apiUrl}/shipment-work/notices/${id}/confirm`,
      { zoneId, noScanReason, idempotencyKey }, { params: this.companyParams() });
  }

  resolveInboundException(id: number, exceptionId: number, reason: string, zoneId?: number):
      Observable<APIResponseWrapper<WarehousingInboundNotice>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundNotice>>(
      `${this.apiUrl}/shipment-work/notices/${id}/exceptions/${exceptionId}/resolve`,
      { reason, zoneId }, { params: this.companyParams() });
  }

  verifyInboundNotice(id: number): Observable<APIResponseWrapper<WarehousingInboundNotice>> {
    return this.http.post<APIResponseWrapper<WarehousingInboundNotice>>(`${this.apiUrl}/shipment-work/notices/${id}/verify`,
      {}, { params: this.companyParams() });
  }

  previewShipmentBulkWork(request: WarehousingBulkRequest): Observable<APIResponseWrapper<WarehousingBulkPreview>> {
    return this.http.post<APIResponseWrapper<WarehousingBulkPreview>>(`${this.apiUrl}/shipment-work/bulk/preview`,
      request, { params: this.companyParams() });
  }

  commitShipmentBulkWork(command: { previewToken: string; reason: string; idempotencyKey: string; correlationId: string }):
      Observable<APIResponseWrapper<WarehousingBulkResult>> {
    return this.http.post<APIResponseWrapper<WarehousingBulkResult>>(`${this.apiUrl}/shipment-work/bulk/commit`,
      command, { params: this.companyParams() });
  }

  getShipmentWorkHistory(documentType: 'TRANSFER' | 'DISPATCH', documentId: number):
      Observable<APIResponseWrapper<WarehousingWorkHistory[]>> {
    return this.http.get<APIResponseWrapper<WarehousingWorkHistory[]>>(
      `${this.apiUrl}/shipment-work/history/${documentType}/${documentId}`, { params: this.companyParams() });
  }

  getTransfers(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingTransfer[]>> {
    return this.http.get<APIResponseWrapper<WarehousingTransfer[]>>(`${this.apiUrl}/transfers`, { params: this.searchParams(filters) });
  }

  getTransfer(transferId: number): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.get<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}`, { params: this.companyParams() });
  }

  createTransfer(transfer: WarehousingTransfer): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers`, this.normalizeDates(transfer), { params: this.companyParams() });
  }

  reserveTransfer(transferId: number): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/reserve`, {}, { params: this.companyParams() });
  }

  markTransferPicked(transferId: number, reason: string): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/mark-picked`, { reason }, { params: this.companyParams() });
  }

  markTransferStaged(transferId: number, reason: string): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/mark-staged`, { reason }, { params: this.companyParams() });
  }

  markTransferLoaded(transferId: number, reason: string): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/mark-loaded`, { reason }, { params: this.companyParams() });
  }

  scanTransferPick(transferId: number, command: WarehousingTransferScanCommand): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/scan-pick`,
      command, { params: this.companyParams() });
  }

  scanTransferStage(transferId: number, command: WarehousingTransferScanCommand): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/scan-stage`,
      command, { params: this.companyParams() });
  }

  scanTransferLoad(transferId: number, command: WarehousingTransferScanCommand): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/scan-load`,
      command, { params: this.companyParams() });
  }

  confirmTransferDispatch(transferId: number, reason: string): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/confirm-dispatch`, { reason }, { params: this.companyParams() });
  }

  scanTransferReceipt(transferId: number, command: WarehousingTransferScanCommand): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/scan-receipt`,
      command, { params: this.companyParams() });
  }

  cancelTransfer(transferId: number): Observable<APIResponseWrapper<WarehousingTransfer>> {
    return this.http.post<APIResponseWrapper<WarehousingTransfer>>(`${this.apiUrl}/transfers/${transferId}/cancel`, {}, { params: this.companyParams() });
  }

  printStockUnitLabels(request: WarehousingStockUnitLabelRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/labels/stock-units/zpl`, request, {
      params: this.companyParams(),
      responseType: 'blob'
    });
  }

  getRefurbishments(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingRefurbishment[]>> {
    return this.http.get<APIResponseWrapper<WarehousingRefurbishment[]>>(`${this.apiUrl}/refurbishments`, { params: this.searchParams(filters) });
  }

  createRefurbishment(refurbishment: WarehousingRefurbishment): Observable<APIResponseWrapper<WarehousingRefurbishment>> {
    return this.http.post<APIResponseWrapper<WarehousingRefurbishment>>(`${this.apiUrl}/refurbishments`, this.normalizeDates(refurbishment), { params: this.companyParams() });
  }

  getDestructions(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingDestruction[]>> {
    return this.http.get<APIResponseWrapper<WarehousingDestruction[]>>(`${this.apiUrl}/destructions`, { params: this.searchParams(filters) });
  }

  getDestruction(destructionId: number): Observable<APIResponseWrapper<WarehousingDestruction>> {
    return this.http.get<APIResponseWrapper<WarehousingDestruction>>(`${this.apiUrl}/destructions/${destructionId}`, { params: this.companyParams() });
  }

  createDestruction(destruction: WarehousingDestruction): Observable<APIResponseWrapper<WarehousingDestruction>> {
    return this.http.post<APIResponseWrapper<WarehousingDestruction>>(`${this.apiUrl}/destructions`, this.normalizeDates(destruction), { params: this.companyParams() });
  }

  confirmDestruction(destructionId: number): Observable<APIResponseWrapper<WarehousingDestruction>> {
    return this.http.post<APIResponseWrapper<WarehousingDestruction>>(`${this.apiUrl}/destructions/${destructionId}/confirm`, {}, { params: this.companyParams() });
  }

  cancelDestruction(destructionId: number): Observable<APIResponseWrapper<WarehousingDestruction>> {
    return this.http.post<APIResponseWrapper<WarehousingDestruction>>(`${this.apiUrl}/destructions/${destructionId}/cancel`, {}, { params: this.companyParams() });
  }

  getStorageRates(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingStorageRate[]>> {
    return this.http.get<APIResponseWrapper<WarehousingStorageRate[]>>(`${this.apiUrl}/storage-rates`, { params: this.searchParams(filters) });
  }

  saveStorageRate(rate: WarehousingStorageRate): Observable<APIResponseWrapper<WarehousingStorageRate>> {
    const payload = this.normalizeDates(rate);
    if (rate.id) {
      return this.http.put<APIResponseWrapper<WarehousingStorageRate>>(`${this.apiUrl}/storage-rates/${rate.id}`, payload, { params: this.companyParams() });
    }
    return this.http.post<APIResponseWrapper<WarehousingStorageRate>>(`${this.apiUrl}/storage-rates`, payload, { params: this.companyParams() });
  }

  getBillingDrafts(filters?: WarehousingSearchFilters): Observable<APIResponseWrapper<WarehousingBillingDraft[]>> {
    return this.http.get<APIResponseWrapper<WarehousingBillingDraft[]>>(`${this.apiUrl}/billing-drafts`, { params: this.searchParams(filters) });
  }

  generateBillingDraft(draft: WarehousingBillingDraft): Observable<APIResponseWrapper<WarehousingBillingDraft>> {
    return this.http.post<APIResponseWrapper<WarehousingBillingDraft>>(`${this.apiUrl}/billing-drafts/generate`, this.normalizeDates(draft), { params: this.companyParams() });
  }

  updateBillingDraft(draft: WarehousingBillingDraft): Observable<APIResponseWrapper<WarehousingBillingDraft>> {
    return this.http.put<APIResponseWrapper<WarehousingBillingDraft>>(`${this.apiUrl}/billing-drafts/${draft.id}`, this.normalizeDates(draft), { params: this.companyParams() });
  }

  postBillingDraft(draftId: number): Observable<APIResponseWrapper<WarehousingBillingDraft>> {
    return this.http.post<APIResponseWrapper<WarehousingBillingDraft>>(`${this.apiUrl}/billing-drafts/${draftId}/post-invoice`, {}, { params: this.companyParams() });
  }

  private companyParams(): HttpParams {
    return new HttpParams().set('companyId', this.getCompanyId());
  }

  private searchParams(filters?: WarehousingSearchFilters): HttpParams {
    let params = this.companyParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.filter(item => item !== null && item !== undefined && item !== '').forEach(item => {
            params = params.append(key, String(item));
          });
        } else if (value instanceof Date) {
          params = params.set(key, this.toLocalDate(value));
        } else {
          params = params.set(key, String(value));
        }
      }
    });
    return params;
  }

  private normalizeDates<T>(payload: T): T {
    return JSON.parse(JSON.stringify(payload, (_key, value) => {
      if (value instanceof Date) {
        return this.toLocalDate(value);
      }
      return value;
    }));
  }

  private toLocalDate(date: Date): string {
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60_000);
    return local.toISOString().slice(0, 10);
  }
}
